import type { Scene } from '@babylonjs/core/scene';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import '@babylonjs/core/Particles/particleSystemComponent';
import type { DustBurstConfig } from '../config/events.config';
import { createDustSprite } from './DustEffect';

/**
 * One-shot dust clouds at arbitrary world points (e.g. where collapse debris lands). Particles live in
 * world space; each burst moves the emitter and emits `perImpact` particles at once.
 */
export class DustBurst {
  private readonly system: ParticleSystem;
  private readonly origin = new Vector3();

  constructor(scene: Scene, private readonly config: DustBurstConfig) {
    const c = config;
    const system = (this.system = new ParticleSystem('FX_DUST_BURST', c.capacity, scene));
    system.particleTexture = createDustSprite(scene);
    system.emitter = this.origin;
    system.minEmitBox = new Vector3(-c.area.halfWidth, 0, -c.area.halfDepth);
    system.maxEmitBox = new Vector3(c.area.halfWidth, c.area.height, c.area.halfDepth);
    system.direction1 = new Vector3(-1, 0.4, -1);
    system.direction2 = new Vector3(1, 1.4, 1);
    system.minEmitPower = c.speed.min;
    system.maxEmitPower = c.speed.max;
    system.gravity = new Vector3(0, c.rise, 0);
    system.minSize = c.size.min;
    system.maxSize = c.size.max;
    system.addSizeGradient(0, 1);
    system.addSizeGradient(1, c.size.growth);
    system.minLifeTime = c.lifetime.min;
    system.maxLifeTime = c.lifetime.max;
    system.minAngularSpeed = -0.6;
    system.maxAngularSpeed = 0.6;
    system.color1 = new Color4(...c.color);
    system.color2 = new Color4(...c.color);
    system.colorDead = new Color4(...c.colorFade);
    system.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    system.emitRate = 0;
    system.start();
  }

  burst(at: Vector3): void {
    this.origin.copyFrom(at);
    this.system.manualEmitCount = this.config.perImpact;
  }

  reset(): void {
    this.system.reset();
  }

  dispose(): void {
    this.system.dispose();
  }
}
